import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SbTable, SbNotification, SbEmpty, SbButton } from "uiCore";
import { getViewPlanningPaicRedesignRoleEmployees } from "Api/apiService";
import { buildPersonColumns, buildScoreColumn } from "../../RedeploymentCenterV2/RedeploymentCenterV2.utils";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import { NESTED_TABLE_SCROLL_HEIGHT } from "../../talentMobility.constants";
import { NestedTableWrapperStyled } from "../../TalentMobilityManagerV2/TalentMobilityManagerV2.styles";
import RolePanelCollapse from "../../components/RolePanelCollapse";
import { tableEmptyLocale } from "../ViewPlanningV2.utils";
import { STICKY_PAGINATION_HEIGHT } from "../../sharedTable.styles";
import { ROLES_ADDED_API_STATUS } from "../../PaicOrgRedesignV2.utils";

// Below this the list is too cramped to use — a very short screen scrolls
// the outer table instead.
const MIN_NESTED_BODY_HEIGHT = 160;

// The nested list's scroll height, sized so the whole panel fits in the room
// visible below its top edge inside the role table's scroll area
// ([data-nested-scroll-root]) when it opens, so only the list scrolls.
// Re-measured when that area or its content (rows above opening/closing) resizes.
const useFittedBodyHeight = (panelRef, isOpen, contentKey) => {
  const [bodyHeight, setBodyHeight] = useState(NESTED_TABLE_SCROLL_HEIGHT);

  useLayoutEffect(() => {
    const panelEl = panelRef.current;
    const scrollRoot = panelEl?.closest("[data-nested-scroll-root]");
    if (!isOpen || !panelEl || !scrollRoot) return undefined;

    const measure = () => {
      const bodyEl = panelEl.querySelector(".ant-table-body");
      if (!bodyEl) return;
      const rootRect = scrollRoot.getBoundingClientRect();
      const panelRect = panelEl.getBoundingClientRect();
      // Visible-area coordinates: a panel opened lower down still gets the
      // room below it on screen, not its offset in the scrolled content.
      const panelTop = panelRect.top - rootRect.top;
      const chromeAbove = bodyEl.getBoundingClientRect().top - panelRect.top;
      const chromeBelow = parseFloat(window.getComputedStyle(panelEl).paddingBottom) || 0;
      const reservedBottom = scrollRoot.querySelector(".ant-table-pagination") ? STICKY_PAGINATION_HEIGHT : 0;
      const available = scrollRoot.clientHeight - panelTop - chromeAbove - chromeBelow - reservedBottom;
      setBodyHeight(Math.max(MIN_NESTED_BODY_HEIGHT, Math.floor(available)));
    };

    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(scrollRoot);
    if (scrollRoot.firstElementChild) observer.observe(scrollRoot.firstElementChild);
    return () => observer.disconnect();
  }, [panelRef, isOpen, contentKey]);

  return bodyHeight;
};

// PAIC Redesign Plan ▸ organization ▸ role, expanded under the role row: the
// employees the plan shortlisted for it — read-only rows, no further drill-in.
// Reduced role → its own people, scored by SRI. Added (INCREASED) role →
// candidates picked to fill it, scored by SMI.
const PaicRoleEmployeesPanel = ({ planId, orgRedesignId, role, isOpen }) => {
  const navigate = useNavigate();
  const thresholds = useMatchThresholds();
  const [employees, setEmployees] = useState(null); // null = loading
  const roleKey = role.role_key;
  const roleJdId = role.jd_sb_unique_id || roleKey;
  const panelRef = useRef(null);
  const isIncreasedRole = role.change_type === ROLES_ADDED_API_STATUS;
  const scoreIndex = isIncreasedRole ? "SMI" : "SRI";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getViewPlanningPaicRedesignRoleEmployees(planId, orgRedesignId, roleKey);
        if (!cancelled) setEmployees(Array.isArray(response?.employees) ? response.employees : []);
      } catch (error) {
        if (cancelled) return;
        setEmployees([]);
        SbNotification.error(
          "Unable to load shortlisted employees",
          error?.response?.data?.message || "Please try again."
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [planId, orgRedesignId, roleKey]);

  const rows = useMemo(
    () =>
      (employees || []).map((employee) => ({
        person: { id: employee.employee_unique_id, employeeId: employee.emp_id, name: employee.name },
        // The API's `smi` is the profile score against this role (SRI or SMI,
        // see scoreIndex); null when never scored, which renders as a dash.
        score: employee.smi == null ? null : Math.round(employee.smi),
      })),
    [employees]
  );

  const columns = useMemo(
    () => [
      ...buildPersonColumns(rows, { nameTitle: "Name" }),
      buildScoreColumn(scoreIndex, "score", thresholds, {
        index: scoreIndex,
        largeTag: true,
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
      }),
      {
        title: "",
        key: "viewProfile",
        align: "center",
        render: (_, record) => (
          <SbButton
            buttonType="secondary"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              // flow=view-planning: a read-only visit — no Shortlist action.
              navigate(
                `/talent-mobility/candidate/${encodeURIComponent(record.person.id)}/role/${encodeURIComponent(
                  roleJdId
                )}?index=smi&flow=view-planning`
              );
            }}
          >
            View Profile
          </SbButton>
        ),
      },
    ],
    [rows, thresholds, navigate, roleJdId, scoreIndex]
  );

  const isLoading = employees === null;
  const bodyHeight = useFittedBodyHeight(panelRef, isOpen, rows.length);

  return (
    <RolePanelCollapse isOpen={isOpen}>
      <NestedTableWrapperStyled ref={panelRef}>
        <div className="nested-title">Shortlisted employees</div>
        {!isLoading && rows.length === 0 ? (
          <SbEmpty description="No employees were shortlisted for this role in this plan." />
        ) : (
          <SbTable
            columns={columns}
            dataSource={rows}
            rowKey={(record) => record.person.id}
            pagination={false}
            scroll={{ y: bodyHeight }}
            // Loading spinner only; once loaded with nobody, SbEmpty replaces the table.
            locale={tableEmptyLocale(isLoading ? null : rows, "")}
          />
        )}
      </NestedTableWrapperStyled>
    </RolePanelCollapse>
  );
};

export default PaicRoleEmployeesPanel;
